/* NotesSearchDialog — corporate search across ALL call notes by keyword,
   AI-insight text or visitor name. Clicking a result opens that call's
   full notes (CallNotesDialog). */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Loader2, Search, Sparkles, X } from "lucide-react";
import { Input } from "../ui/input";
import CallNotesDialog from "./CallNotesDialog";
import logging from "../../utils/logging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotesSearchDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openRing, setOpenRing] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setResults(null); setOpenRing(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return undefined; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await axios.get(`${API}/doorbell/notes/search`, {
          params: { q: q.trim() },
          withCredentials: true,
        });
        setResults(r.data.results || []);
      } catch (err) {
        logging.error("Notes search", err);
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center" data-testid="notes-search-dialog">
      <div className="bg-white text-gray-900 w-full sm:max-w-lg sm:rounded-sm max-h-[85dvh] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search notes by keyword or visitor…"
            className="rounded-sm h-9 text-sm border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid="notes-search-input"
          />
          {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />}
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100 shrink-0" data-testid="notes-search-close" aria-label="Close search">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[160px]">
          {results === null ? (
            <p className="text-xs text-gray-400 italic font-body">
              Type at least 2 characters — searches note text, AI insights and visitor names.
            </p>
          ) : results.length === 0 ? (
            <p className="text-xs text-gray-400 italic font-body" data-testid="notes-search-empty">No notes match "{q}".</p>
          ) : results.map((n) => (
            <button
              key={n.note_id}
              onClick={() => setOpenRing({ ring_id: n.ring_id, visitor_name: n.visitor_name })}
              className="w-full text-left border border-gray-200 rounded-sm p-2.5 hover:border-gray-400 transition-colors"
              data-testid={`notes-search-result-${n.note_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-body font-bold text-gray-800 truncate">{n.visitor_name || "Visitor"}</span>
                <span className="text-[10px] text-gray-400 font-body shrink-0">{new Date(n.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-[11px] font-body text-gray-600 mt-0.5 line-clamp-2">{n.text}</p>
              {n.ai_insight && (
                <span className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-widest font-bold text-violet-700">
                  <Sparkles className="w-2.5 h-2.5" /> AI insight
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {openRing && (
        <CallNotesDialog
          ringId={openRing.ring_id}
          visitorName={openRing.visitor_name}
          open
          onClose={() => setOpenRing(null)}
        />
      )}
    </div>,
    document.body
  );
}
